import { create } from 'zustand';

// Helper to safely read sessionStorage
const getSessionItem = (key) => {
  try {
    const val = sessionStorage.getItem(key);
    return val ? JSON.parse(val) : null;
  } catch {
    return null;
  }
};

// Global app store using Zustand
const useAppStore = create((set) => ({
  // Tenant branding
  tenantConfig: null,
  setTenantConfig: (config) => set({ tenantConfig: config }),
  
  // Current user
  currentUser: (() => {
    try {
      const user = localStorage.getItem('user');
      return user ? JSON.parse(user) : null;
    } catch {
      return null;
    }
  })(),
  setCurrentUser: (user) => set({ currentUser: user }),
  
  // Notifications
  notifications: [],
  unreadCount: 0,
  addNotification: (notification) => set((state) => ({
    notifications: [notification, ...state.notifications],
    unreadCount: state.unreadCount + 1,
  })),
  markNotificationRead: (id) => set((state) => ({
    notifications: state.notifications.map((n) =>
      n.id === id ? { ...n, read: true } : n
    ),
    unreadCount: Math.max(0, state.unreadCount - 1),
  })),
  markAllNotificationsRead: () => set((state) => ({
    notifications: state.notifications.map((n) => ({ ...n, read: true })),
    unreadCount: 0,
  })),
  
  // Offline queue for technician PWA
  offlineQueue: [],
  addToOfflineQueue: (item) => set((state) => ({
    offlineQueue: [...state.offlineQueue, item],
  })),
  removeFromOfflineQueue: (id) => set((state) => ({
    offlineQueue: state.offlineQueue.filter((item) => item.id !== id),
  })),
  clearOfflineQueue: () => set({ offlineQueue: [] }),
  
  // UI state
  sidebarOpen: true,
  toggleSidebar: () => set((state) => ({ sidebarOpen: !state.sidebarOpen })),
  setSidebarOpen: (open) => set({ sidebarOpen: open }),
  
  // Admin "View As" role switching — persisted to sessionStorage
  viewAsRole: getSessionItem('viewAsRole'),
  viewAsUser: getSessionItem('viewAsUser'),
  setViewAsRole: (role) => {
    sessionStorage.setItem('viewAsRole', JSON.stringify(role));
    sessionStorage.removeItem('viewAsUser');
    set({ viewAsRole: role, viewAsUser: null });
  },
  setViewAsUser: (user) => {
    sessionStorage.setItem('viewAsUser', JSON.stringify(user));
    set({ viewAsUser: user });
  },
  clearViewAs: () => {
    sessionStorage.removeItem('viewAsRole');
    sessionStorage.removeItem('viewAsUser');
    set({ viewAsRole: null, viewAsUser: null });
  },
  
  // Sound notification preference
  soundEnabled: (() => {
    try {
      const val = localStorage.getItem('soundEnabled');
      return val !== null ? JSON.parse(val) : true;
    } catch {
      return true;
    }
  })(),
  toggleSound: () => set((state) => {
    const next = !state.soundEnabled;
    localStorage.setItem('soundEnabled', JSON.stringify(next));
    return { soundEnabled: next };
  }),
}));

export default useAppStore;